import { useEffect, useMemo, useState } from "react";
import {
  Delete,
  LoaderCircle,
  LockKeyhole,
  RefreshCw,
  UserRound,
} from "lucide-react";

import { NovaBrand } from "./components/NovaBrand";

export type WebPosStaff = {
  id: string;
  displayName: string;
  role: string;
};

type StaffListItem = WebPosStaff & {
  lockedUntil: string | null;
};

type Props = {
  onSignedIn: (staff: WebPosStaff) => void;
};

const KEYPAD = ["1", "2", "3", "4", "5", "6", "7", "8", "9"];

function isLocked(staff: StaffListItem) {
  if (!staff.lockedUntil) {
    return false;
  }

  return new Date(staff.lockedUntil).getTime() > Date.now();
}

export function NovaPosLogin({ onSignedIn }: Props) {
  const [staff, setStaff] = useState<StaffListItem[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [pin, setPin] = useState("");
  const [loadingStaff, setLoadingStaff] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = useMemo(
    () => staff.find((item) => item.id === selectedId) ?? null,
    [staff, selectedId],
  );

  async function loadStaff() {
    setLoadingStaff(true);
    setError(null);

    try {
      const response = await fetch("/api/pos/staff", {
        credentials: "include",
        cache: "no-store",
      });

      if (!response.ok) {
        throw new Error("Staff list is unavailable.");
      }

      const payload = (await response.json()) as {
        staff?: StaffListItem[];
      };

      setStaff(Array.isArray(payload.staff) ? payload.staff : []);
    } catch {
      setError(
        "Nova Web POS could not reach the secure staff service.",
      );
    } finally {
      setLoadingStaff(false);
    }
  }

  useEffect(() => {
    void loadStaff();
  }, []);

  function chooseStaff(item: StaffListItem) {
    if (isLocked(item)) {
      return;
    }

    setSelectedId(item.id);
    setPin("");
    setError(null);
  }

  function appendDigit(digit: string) {
    if (!selected || submitting || pin.length >= 12) {
      return;
    }

    setPin((current) => current + digit);
    setError(null);
  }

  function deleteDigit() {
    setPin((current) => current.slice(0, -1));
    setError(null);
  }

  async function openStation() {
    if (!selected || pin.length < 4 || submitting) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const response = await fetch("/api/pos/login", {
        method: "POST",
        credentials: "include",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify({
          staffUserId: selected.id,
          pin,
        }),
      });

      const payload = (await response.json()) as {
        staff?: WebPosStaff;
        error?: string;
        attemptsRemaining?: number;
        lockedUntil?: string | null;
      };

      if (!response.ok || !payload.staff) {
        setPin("");

        if (payload.error === "TEMPORARILY_LOCKED") {
          setError(
            "Too many incorrect attempts. This PIN is temporarily locked for 15 minutes.",
          );
          await loadStaff();
          return;
        }

        if (
          payload.error === "INVALID_PIN" &&
          typeof payload.attemptsRemaining === "number"
        ) {
          setError(
            `Incorrect PIN. ${payload.attemptsRemaining} attempt${
              payload.attemptsRemaining === 1 ? "" : "s"
            } remaining before temporary lockout.`,
          );
          return;
        }

        setError("That PIN was not accepted.");
        return;
      }

      setPin("");
      onSignedIn(payload.staff);
    } catch {
      setPin("");
      setError(
        "Nova Web POS could not reach the secure login service.",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="min-h-[100dvh] bg-[#F6F1E8] px-4 py-6 text-[#193B3F]">
      <div className="mx-auto grid min-h-[calc(100dvh-3rem)] max-w-6xl place-items-center">
        <div className="grid w-full overflow-hidden rounded-[28px] border border-[#DDD3C2] bg-[#FFFCF6] shadow-[0_24px_80px_rgba(25,59,63,.16)] lg:grid-cols-[1fr_470px]">
          <section className="relative overflow-hidden bg-[#215E66] p-7 text-[#FFFDF8] sm:p-10">
            <NovaBrand />

            <p className="mt-10 text-[10px] font-extrabold uppercase tracking-[.24em] text-[#F5C242]">
              Online Operations · Staff Access
            </p>

            <h1 className="mt-3 max-w-lg text-4xl font-bold leading-tight sm:text-5xl">
              Your station. Your actions. Your service.
            </h1>

            <p className="mt-4 max-w-lg text-sm leading-6 text-white/65">
              Select your name and enter your private PIN. Every protected
              action remains attributable to the signed-in staff member.
            </p>

            <div className="mt-10 rounded-2xl border border-white/10 bg-white/[.06] p-5">
              <p className="text-[10px] font-bold uppercase tracking-[.18em] text-white/45">
                Nova Web POS
              </p>

              <p className="mt-2 font-bold">
                Online extension of the Restaurant Edge
              </p>

              <p className="mt-2 text-xs leading-5 text-white/55">
                Staff accounts and PINs do not expire with time. Security
                lockouts are temporary, while the Main PC remains the
                restaurant&apos;s Edge authority.
              </p>
            </div>
          </section>

          <section className="p-6 sm:p-8">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-extrabold uppercase tracking-[.18em] text-[#62787A]">
                  Staff Access
                </p>

                <p className="mt-1 text-sm font-bold">
                  Who is using this station?
                </p>
              </div>

              <button
                type="button"
                onClick={() => void loadStaff()}
                disabled={loadingStaff}
                className="grid h-10 w-10 place-items-center rounded-xl border border-[#DDD3C2] bg-[#F8F4EC] disabled:opacity-40"
                aria-label="Refresh staff"
              >
                <RefreshCw
                  className={`h-4 w-4 ${
                    loadingStaff ? "animate-spin" : ""
                  }`}
                />
              </button>
            </div>

            <div className="mt-5 grid max-h-52 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
              {loadingStaff ? (
                <div className="col-span-full flex min-h-24 items-center justify-center rounded-xl border border-[#DDD3C2] bg-[#F8F4EC]">
                  <LoaderCircle className="h-5 w-5 animate-spin" />
                </div>
              ) : null}

              {!loadingStaff && staff.length === 0 ? (
                <div className="col-span-full rounded-xl border border-[#DDD3C2] bg-[#F8F4EC] p-4 text-sm">
                  No active staff accounts are available.
                </div>
              ) : null}

              {!loadingStaff
                ? staff.map((item) => {
                    const locked = isLocked(item);
                    const active = selectedId === item.id;

                    return (
                      <button
                        key={item.id}
                        type="button"
                        disabled={locked}
                        onClick={() => chooseStaff(item)}
                        className={[
                          "min-h-20 rounded-xl border p-3 text-left transition",
                          active
                            ? "border-[#F5C242] bg-[#FFF5D2]"
                            : "border-[#DDD3C2] bg-[#F8F4EC]",
                          locked
                            ? "cursor-not-allowed opacity-45"
                            : "hover:border-[#C3AD7E]",
                        ].join(" ")}
                      >
                        <div className="flex items-start gap-3">
                          <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[#215E66] text-white">
                            {locked ? (
                              <LockKeyhole className="h-4 w-4" />
                            ) : (
                              <UserRound className="h-4 w-4" />
                            )}
                          </div>

                          <div className="min-w-0">
                            <p className="truncate text-sm font-extrabold">
                              {item.displayName}
                            </p>
                            <p className="mt-1 text-[10px] font-bold uppercase tracking-[.14em] text-[#62787A]">
                              {locked
                                ? "Temporarily locked"
                                : item.role}
                            </p>
                          </div>
                        </div>
                      </button>
                    );
                  })
                : null}
            </div>

            <div className="mt-6 border-t border-[#E5DCCB] pt-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] font-extrabold uppercase tracking-[.16em] text-[#62787A]">
                    Staff PIN
                  </p>

                  <p className="mt-1 text-sm font-bold">
                    {selected
                      ? selected.displayName
                      : "Select your name first"}
                  </p>
                </div>

                <div className="flex min-h-10 min-w-28 items-center justify-center gap-2 rounded-xl border border-[#DDD3C2] bg-[#F8F4EC] px-3">
                  {pin.length === 0 ? (
                    <span className="text-xs font-bold text-[#9A9A90]">
                      PIN
                    </span>
                  ) : (
                    Array.from({ length: pin.length }).map((_, index) => (
                      <span
                        key={index}
                        className="h-2.5 w-2.5 rounded-full bg-[#215E66]"
                      />
                    ))
                  )}
                </div>
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2">
                {KEYPAD.map((digit) => (
                  <button
                    key={digit}
                    type="button"
                    disabled={!selected || submitting}
                    onClick={() => appendDigit(digit)}
                    className="min-h-14 rounded-xl border border-[#DDD3C2] bg-[#F8F4EC] text-lg font-extrabold transition hover:border-[#C3AD7E] disabled:opacity-30"
                  >
                    {digit}
                  </button>
                ))}

                <button
                  type="button"
                  disabled={!selected || submitting}
                  onClick={() => {
                    setPin("");
                    setError(null);
                  }}
                  className="min-h-14 rounded-xl border border-[#DDD3C2] bg-[#F8F4EC] text-xs font-extrabold uppercase tracking-[.1em] disabled:opacity-30"
                >
                  Clear
                </button>

                <button
                  type="button"
                  disabled={!selected || submitting}
                  onClick={() => appendDigit("0")}
                  className="min-h-14 rounded-xl border border-[#DDD3C2] bg-[#F8F4EC] text-lg font-extrabold disabled:opacity-30"
                >
                  0
                </button>

                <button
                  type="button"
                  disabled={!selected || submitting}
                  onClick={deleteDigit}
                  className="grid min-h-14 place-items-center rounded-xl border border-[#DDD3C2] bg-[#F8F4EC] disabled:opacity-30"
                  aria-label="Delete PIN digit"
                >
                  <Delete className="h-5 w-5" />
                </button>
              </div>

              {error ? (
                <div className="mt-4 rounded-xl border border-red-500/20 bg-red-500/5 p-3 text-sm font-semibold text-red-700">
                  {error}
                </div>
              ) : null}

              <button
                type="button"
                disabled={!selected || pin.length < 4 || submitting}
                onClick={() => void openStation()}
                className="mt-4 flex min-h-14 w-full items-center justify-center gap-2 rounded-xl bg-[#F5C242] px-4 text-sm font-extrabold text-[#193B3F] disabled:opacity-40"
              >
                {submitting ? (
                  <LoaderCircle className="h-4 w-4 animate-spin" />
                ) : (
                  <LockKeyhole className="h-4 w-4" />
                )}

                {submitting ? "Opening station…" : "Open station"}
              </button>
            </div>
          </section>
        </div>
      </div>
    </main>
  );
}
